import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { createLogger, toErrorMessage } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';
import { DataTable } from '../data-table/data-table.tsx';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.tsx';
import { EndConsumerDrawer } from '../endConsumer/EndConsumerDrawer.tsx';
import { ImportEndConsumerInvitesModal } from '../endConsumer/ImportEndConsumerInvitesModal.tsx';
import { getStatusBadge } from '../endConsumer/statusBadge.tsx';
import {
  type EndConsumerListItem,
  type EndConsumerStatus,
  useEndConsumers,
} from '../../hooks/useEndConsumers.ts';
import { useTableUrlState } from '../../hooks/useTableUrlState.ts';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { FileUp, MoreHorizontal, Search, UserPlus } from 'lucide-react';
import { formatFullTimestamp, formatRelativeTime } from '../../lib/date.ts';

const logger = createLogger('CustomerConsumersTab');

type EndConsumerStatusFilter = EndConsumerStatus | 'all';

// ---------------------------------------------------------------------------
// RemoveConsumerDialog
// ---------------------------------------------------------------------------

interface RemoveConsumerDialogProps {
  open: boolean;
  consumer: EndConsumerListItem | null;
  onOpenChange: (open: boolean) => void;
  onRemove: (email: string) => Promise<void>;
}

function RemoveConsumerDialog({
  open,
  consumer,
  onOpenChange,
  onRemove,
}: RemoveConsumerDialogProps) {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleConfirm = async () => {
    if (!consumer) return;
    setIsRemoving(true);
    try {
      await onRemove(consumer.email);
      toast.success('Consumer removed');
      onOpenChange(false);
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to remove consumer');
      toast.error(message);
      logger.error({ error }, 'Failed to remove consumer');
    } finally {
      setIsRemoving(false);
    }
  };

  if (!consumer) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Consumer?</AlertDialogTitle>
          <AlertDialogDescription>
            This will revoke access for <strong>{consumer.email}</strong>{' '}
            and remove their meter assignments. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isRemoving}>
            {isRemoving ? 'Removing...' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------

/**
 * MyWaterScope Users tab of the customer details page: the customer's
 * end-consumer (MyWaterScope) accounts. Its table state lives under the `consumers` URL
 * prefix so it never collides with the Meters tab's unprefixed keys.
 */
export function CustomerConsumersTab({ customerId }: { customerId: string }) {
  const tableState = useTableUrlState({ defaultPageSize: 50, prefix: 'consumers' });

  const queryClient = useQueryClient();
  const { consumers, isLoading, error, removeEndConsumer, sendInviteEmail } = useEndConsumers(
    customerId,
  );

  const handleSendInviteEmail = useCallback(async (email: string) => {
    try {
      await sendInviteEmail(email);
      toast.success(`Onboarding email sent to ${email}`);
    } catch (err) {
      toast.error(toErrorMessage(err, 'Failed to send onboarding email'));
      logger.error({ error: err }, 'Failed to send onboarding email');
    }
  }, [sendInviteEmail]);

  const [selectedConsumer, setSelectedConsumer] = useState<EndConsumerListItem | null>(null);
  // The create/edit drawer: Add opens it with selectedConsumer=null, Edit with the row.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EndConsumerStatusFilter>('all');

  const handleImportComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['end-consumers', customerId] });
  }, [queryClient, customerId]);

  const filteredConsumers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return consumers.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!query) return true;
      return c.email.toLowerCase().includes(query);
    });
  }, [consumers, searchQuery, statusFilter]);

  const columns: ColumnDef<EndConsumerListItem>[] = [
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
      cell: ({ row }) => <div className='font-medium'>{row.getValue('email')}</div>,
    },
    {
      id: 'status',
      accessorFn: (row) => row.status,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      id: 'meters',
      accessorFn: (row) => row.meterCount,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Meters' />,
      cell: ({ row }) => <span className='text-sm'>{row.original.meterCount}</span>,
    },
    {
      id: 'lastLogin',
      accessorFn: (row) => row.lastLoginAt ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Last Login' />,
      cell: ({ row }) => {
        const lastLoginAt = row.original.lastLoginAt;
        if (!lastLoginAt) {
          return <span className='text-xs text-muted-foreground'>&mdash;</span>;
        }
        return (
          <span
            className='text-xs text-muted-foreground'
            title={formatFullTimestamp(lastLoginAt)}
          >
            {formatRelativeTime(lastLoginAt)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className='text-right'>Actions</div>,
      cell: ({ row }) => {
        const consumer = row.original;
        return (
          <div className='text-right'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' className='h-7 w-7 p-0'>
                  <span className='sr-only'>Open menu</span>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedConsumer(consumer);
                    setDrawerOpen(true);
                  }}
                >
                  Edit
                </DropdownMenuItem>
                {/* Only Invited consumers still need to sign up. */}
                {consumer.status === 'invited' && (
                  <DropdownMenuItem onClick={() => handleSendInviteEmail(consumer.email)}>
                    Send onboarding email
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className='text-destructive'
                  onClick={() => {
                    setSelectedConsumer(consumer);
                    setRemoveOpen(true);
                  }}
                >
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  if (error) {
    return (
      <div>
        <Card>
          <CardHeader>
            <CardTitle>Error Loading MyWaterScope Users</CardTitle>
            <CardDescription>{String(error)}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8' role='status'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-end'>
        <div className='flex items-center gap-2'>
          <Button variant='outline' onClick={() => setImportOpen(true)}>
            <FileUp className='mr-2 h-4 w-4' />
            Import CSV
          </Button>
          <Button
            onClick={() => {
              setSelectedConsumer(null);
              setDrawerOpen(true);
            }}
          >
            <UserPlus className='mr-2 h-4 w-4' />
            + Add consumer
          </Button>
        </div>
      </div>

      <ImportEndConsumerInvitesModal
        open={importOpen}
        onOpenChange={setImportOpen}
        customerId={Number(customerId)}
        onComplete={handleImportComplete}
      />

      <EndConsumerDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        customerId={customerId}
        consumer={selectedConsumer}
        onRequestRemove={(consumer) => {
          setSelectedConsumer(consumer);
          setRemoveOpen(true);
        }}
      />

      <RemoveConsumerDialog
        open={removeOpen}
        consumer={selectedConsumer}
        onOpenChange={setRemoveOpen}
        onRemove={async (email) => {
          await removeEndConsumer(email);
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>MyWaterScope Users</CardTitle>
          <CardDescription>
            Consumer accounts with access to MyWaterScope for this customer
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center gap-2 flex-wrap'>
            <div className='relative flex-1 min-w-[200px]'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search by email...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-8'
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as EndConsumerStatusFilter)}
            >
              <SelectTrigger className='w-[160px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Statuses</SelectItem>
                <SelectItem value='invited'>Invited</SelectItem>
                <SelectItem value='active'>Active</SelectItem>
                <SelectItem value='disabled'>Disabled</SelectItem>
              </SelectContent>
            </Select>
            {(searchQuery || statusFilter !== 'all') && (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                Clear
              </Button>
            )}
          </div>

          <div className='text-sm text-muted-foreground'>
            Showing {filteredConsumers.length} of {consumers.length} consumers
          </div>

          <DataTable
            columns={columns}
            data={filteredConsumers}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
